import { Fragment, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { useEditor } from '@craftjs/core'
import { cn, ListItem } from 'twyne-new.ui'

import { TailwindSandboxPure } from '../../TailwindSandbox'

export default function VariantAssetItem({ item, floatbarRef }) {
  const [open, setOpen] = useState(false)
  const { element, icon, text, variants, variantsTitle = text } = item
  const { connectors: { create }, actions: { setProp } } = useEditor()

  const handleOpen = useMemo(() => {
    let timeoutId = null

    return open => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
      if (open) {
        setOpen(true)
      } else {
        timeoutId = setTimeout(() => setOpen(false), 100)
      }
    }
  }, [])

  const handleCreateVariant = propsRef => ref => create(ref, element, {
    onCreate: ({ rootNodeId }) => {
      setProp(rootNodeId, prop => {
        Object.assign(prop.properties, propsRef.current)
      })
    },
  })

  return <ListItem
    className={open ? 'w-100' : ''}
    onMouseEnter={() => handleOpen(true)}
    onMouseLeave={() => handleOpen(false)}
    value={<div className='flex items-center gap-2'>
      {icon}
      {text}
      {floatbarRef.current && createPortal(
        <div className={cn('absolute inset-0 z-50 w-112 rounded-xs p-2 twy-bg-base-accent shadow-lg transition-[transform,opacity] duration-200 ease-out', open ? 'translate-x-0 opacity-100' : '-translate-x-3 opacity-0 pointer-events-none')}>
          <TailwindSandboxPure>
            <div className='px-3 pt-2 pb-3 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400'>{variantsTitle}</div>
            <ul className='flex flex-col gap-2'>
              {variants.map(data => <VariantPreview
                key={data.key}
                data={data}
                onDragStart={() => handleOpen(false)}
                onCreate={handleCreateVariant}
              />)}
            </ul>
          </TailwindSandboxPure>
        </div>,
        floatbarRef.current,
      )}
    </div>}
  />
}

function VariantPreview({ data: { key, label, preview: Preview, props: defaultProps, LabelTag = Fragment, badge, hint, card }, onDragStart, onCreate }) {
  const propsRef = useRef(defaultProps)

  return <li
    ref={onCreate(propsRef)}
    onDragStart={onDragStart}
    className={cn('flex items-center gap-3 rounded-md cursor-grab transition-colors', !card && 'px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-800')}
  >
    {badge && <span className='shrink-0 inline-flex h-8 min-w-8 items-center justify-center rounded-sm border border-gray-300 bg-gray-50 px-1.5 font-mono text-xs text-blue-500 dark:border-gray-700 dark:bg-gray-900 dark:text-blue-300'>
      {badge}
    </span>}
    <div className='flex-1 min-w-0'>
      <LabelTag {...(LabelTag !== Fragment ? { label } : {})}>
        <Preview {...defaultProps} label={label} variant={key} onChangeProps={props => {
          propsRef.current = { ...defaultProps, ...props }
        }}>{label}</Preview>
      </LabelTag>
    </div>
    {hint && <span className='shrink-0 text-xs text-gray-500 dark:text-gray-400'>{hint}</span>}
  </li>
}

